import React, { FC, useEffect, useMemo, useState } from "react"
import { observer } from "mobx-react"

import { Form, Input, Select } from "antd"
import {
  NAVIGATION_DEFAULT,
  NAVIGATION_LABELS,
  NAVIGATION_MOBILE,
} from "components/defaultLinksRouting/default-navigation.constants"
import { configItemRoutes } from "components/defaultLinksRouting/configItemRoutes"
import { useStore } from "stores/utils/use-store"
import useAdvertisers from "domains/internal-offerwall/hooks/useAdvertisers"
import { RaffleSelector } from "components/raffleSelector"

export const DefaultLinksRouting: FC<{
  link: string;
  formItemName: string | string[];
  displaySelect?: boolean;
  notRequired?: boolean;
  mobile?: boolean;
  onChange?: (value: string) => void;
}> = observer(({ link, formItemName, displaySelect = true, notRequired, mobile, onChange }) => {
  const [linkSwitch, setlinkSwitch] = useState<
    "external" | "internal-routing" | "raffle" | "promo-popup" | "offerwall" | "copy" | "empty"
  >("external")

  const { dataStore: { promoPopupsStore, rafflesStore, managingJsonStore } } = useStore()
  const { advertisers } = useAdvertisers()
  const formInstance = Form.useFormInstance()


  useEffect(() => {
    const func = async() => {
      if (!promoPopupsStore?.popUps || promoPopupsStore?.popUps?.length === 0) {
        await promoPopupsStore.getData()
      }
      if (!rafflesStore?.raffles || rafflesStore?.raffles?.length === 0) {
        rafflesStore.pagination.limit = 100
      }
      if (!managingJsonStore?.itemTypesFromBackend || managingJsonStore?.itemTypesFromBackend?.length === 0) {
        await managingJsonStore.getData()
      }
    }
    func()
  }, [promoPopupsStore, rafflesStore, managingJsonStore, mobile])

  const internalRoutes = useMemo(() => {
    return [
      ...(mobile ? NAVIGATION_MOBILE : NAVIGATION_DEFAULT).map(action => ({
        value: `internal?id=${action}`,
        title: NAVIGATION_LABELS[action] ?? action,
      }
      )),
      ...(mobile ? [] : configItemRoutes(managingJsonStore?.itemTypesFromBackend)),
    ]
  }, [mobile, managingJsonStore?.itemTypesFromBackend])

  useEffect(() => {
    if (link) {
      if (link.startsWith("promo-popup?id=")) {
        return setlinkSwitch("promo-popup")
      }
      if (link.startsWith("raffle?id=")) {
        return setlinkSwitch("raffle")
      }
      if (link.startsWith("internal?id=")) {
        if (internalRoutes.map(({ value }) => value).includes(link)) return setlinkSwitch("internal-routing")
      }
      if (link.startsWith("offerwall")) {
        return setlinkSwitch("offerwall")
      }
      if (link.startsWith("copy")) {
        return setlinkSwitch("copy")
      }

      return setlinkSwitch("external")
    } else if (notRequired) {
      return setlinkSwitch("empty")
    }
  }, [link, internalRoutes, notRequired])

  return (
    <div
      style={{
        display: "grid",
        gridAutoFlow: "column",
        gridTemplateColumns: "25% 75%",
        gap: "10px",
      }}
    >
      {displaySelect && (
        <div>
          <Select
            onChange={value => {
              setlinkSwitch(value)
              if (value === "empty") {
                formInstance.setFieldValue(formItemName, "")
                onChange?.("")
              }
            }}
            value={linkSwitch}
            style={{ width: "100%" }}
          >
            {notRequired && (
              <Select.Option key="empty" value="empty">
                Empty (no link)
              </Select.Option>
            )}
            <Select.Option key="external" value="external">
              External link
            </Select.Option>
            <Select.Option key="internal-routing" value="internal-routing">
              Internal-routing link
            </Select.Option>
            <Select.Option key="raffle" value="raffle">
              Raffle link
            </Select.Option>
            <Select.Option key="promo-popup" value="promo-popup">
              Promo-popup link
            </Select.Option>
            <Select.Option key="offerwall" value="offerwall">
              Offerwall link
            </Select.Option>
            <Select.Option key="copy" value="copy">
              Copy link
            </Select.Option>
          </Select>
        </div>
      )}
      <Form.Item
        label="Link"
        name={formItemName}
        initialValue={link}
        style={{ width: "100%" }}
        rules={
          notRequired
            ? [{}]
            : [
              {
                required: true,
                message: "Link is required",
              },
            ]
        }
      >
        {linkSwitch === "external" ? (
          <Input value={link} />
        ) : linkSwitch === "empty" ? (
          <Input
            value=""
            disabled
            placeholder="No link selected"
            onChange={() => {
              formInstance.setFieldValue(formItemName, "")
              onChange?.("")
            }}
          />
        ) : linkSwitch === "copy" ? (
          <Input
            value={link}
            onChange={e => {
              const value = e.target.value
              const valueToStore = value ? value.startsWith("copy?id=") ? value : `copy?id=${value}` : ""
              formInstance.setFieldValue(formItemName, valueToStore)
              onChange?.(valueToStore)
            }}
          />
        ) : linkSwitch === "promo-popup" ? (
          <Select value={link}>
            {promoPopupsStore?.popUps?.map(action => (
              <Select.Option
                key={`promo-popup?id=${action.id}`}
                value={`promo-popup?id=${action.id}`}
              >
                {action.title}
              </Select.Option>
            ))}
          </Select>
        ) : linkSwitch === "raffle" ? (
          <RaffleSelector
            value={link?.startsWith("raffle?id=") ? link.replace("raffle?id=", "") : undefined}
            onChange={raffleId => {
              const valueToStore = raffleId ? `raffle?id=${raffleId}` : ""
              formInstance.setFieldValue(formItemName, valueToStore)
              onChange?.(valueToStore)
            }}
            placeholder="Search and select raffle"
          />
        ) : linkSwitch ==="offerwall" ? (
          <Select value={link}>
            {
              advertisers?.map(advertiser => (
                <Select.Option
                  key={`internal?id=offerwall/${advertiser.id}`}
                  value={`internal?id=offerwall/${advertiser.id}`}
                >
                  offerwall/{advertiser.name}
                </Select.Option>
              ))
            }
          </Select>
        ) : (
          <Select value={link}>
            {
              internalRoutes.map(({ value, title }) => (
                <Select.Option key={value} value={value}>
                  {title}
                </Select.Option>
              ))
            }
          </Select>
        )}

      </Form.Item>
    </div>
  )
})
